// FILE: src/pages/Security.js
// Uses the same layout template as ProofOfLife:
// - Desktop: action buttons sit beside the header title
// - Mobile: action buttons move below the hero (beamer) image
// Also:
// - Overrides the master dv-item layout ONLY on this page so the MFA card stacks as rows (fixes hidden content on mobile)
// - Leaves Manager Mode untouched (this page uses auth.currentUser + MFA utilities only)

import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { FiEye } from "react-icons/fi";
import { FaYoutube } from "react-icons/fa";

import MFASettings from "../components/security/MFASettings";
import VideoLightbox from "../components/ui/VideoLightbox";

import { auth } from "../firebase"; // ✅ IMPORTANT: pages/ -> ../firebase (NOT ../../firebase)
import { hasSessionBypass, loadMfaProfile } from "../utils/mfa";

import "../styles/layout.css";

export default function Security({ mfaRequired = false, mfaDaysLeft = 0, mfaEnabled = false }) {
  const [loading, setLoading] = useState(true);
  const [locked, setLocked] = useState(false);
  const [err, setErr] = useState("");

  const [showDrawer, setShowDrawer] = useState(false);
  const [showVideo, setShowVideo] = useState(false);

  const location = useLocation();
  const redirectedFrom = location?.state?.mfaGate ? location.state.from : null;
  const showPolicyBanner = !mfaEnabled && (mfaRequired || (Number.isFinite(mfaDaysLeft) && mfaDaysLeft > 0) || redirectedFrom);


  useEffect(() => {
    let live = true;

    async function check() {
      setLoading(true);
      setErr("");

      try {
        const uid = auth.currentUser?.uid;
        if (!uid) {
          setLoading(false);
          setLocked(true);
          return;
        }

        const profile = await loadMfaProfile(uid);
        const isEnabled = !!profile?.enabled;

        if (!live) return;


        // If MFA isn't enabled yet, don't lock (they need access here to enable it).
        if (!isEnabled) {
          setLocked(false);
          setLoading(false);
          return;
        }

        // MFA enabled: require step-up for this page unless verified this session.
        if (hasSessionBypass(uid)) {
          setLocked(false);
          setLoading(false);
          return;
        }

        setLocked(true);
        setLoading(false);

        // Trigger MFA modal
        window.dispatchEvent(new CustomEvent("mfa:open", { detail: { reason: "security-page" } }));
      } catch (e) {
        if (!live) return;
        setErr(e?.message || "Unable to load security status.");
        // Fail-safe: don’t expose settings if we can’t confirm status
        setLocked(true);
        setLoading(false);
      }
    }

    const onVerified = () => {
      const uid = auth.currentUser?.uid;
      if (!uid) return;
      if (hasSessionBypass(uid)) setLocked(false);
    };

    window.addEventListener("mfa:verified", onVerified);
    check();

    return () => {
      live = false;
      window.removeEventListener("mfa:verified", onVerified);
    };
  }, []);

  const openVerify = () => {
    window.dispatchEvent(new CustomEvent("mfa:open", { detail: { reason: "security-page" } }));
  };

  const ActionsDesktop = () => (
    <div className="dp-help-row">
      <button
        data-allow-readonly
        type="button"
        className="btn dp-help read"
        onClick={() => setShowDrawer(true)}
      >
        <FiEye className="dp-help-ico" /> Read how it works
      </button>
      <button
        data-allow-readonly
        type="button"
        className="btn dp-help see"
        onClick={() => setShowVideo(true)}
      >
        <FaYoutube className="dp-help-ico" /> See how it works
      </button>
    </div>
  );

  const ActionsMobile = ActionsDesktop;

  return (
    <div className="container">
      {/* Header row (matches ProofOfLife) */}
      <div className="header-row">
        <h2>Security</h2>
        <div className="header-actions desktop-only">
          <ActionsDesktop />
        </div>
      </div>

      {/* Hero / beamer (your image path) */}
      <div className="hero-wrap">
        <img src="/lastwish-Security.jpg" alt="Security" className="hero-img" />
      </div>

      {/* Mobile buttons below hero (matches ProofOfLife) */}
      <div className="mobile-only" style={{ margin: "10px 0" }}>
        <ActionsMobile />
      </div>


      {showPolicyBanner ? (
        <div
          className={`mfa-policy-banner ${mfaRequired ? "required" : "warning"}`}
          role="alert"
          aria-live="polite"
        >
          <div className="mfa-policy-row">
            <div>
              <div className="mfa-policy-title">
                {mfaRequired ? "Action required: Enable MFA to continue" : "Reminder: MFA will be required soon"}
              </div>
              <div className="mfa-policy-text">
                {mfaRequired ? (
                  <>
                    Your 14-day setup window has ended, so MFA is now required to keep using your account.
                    {redirectedFrom ? (
                      <span className="mfa-policy-muted"> You were redirected here from <b>{redirectedFrom}</b>.</span>
                    ) : null}
                  </>
                ) : (
                  <>
                    You have <b>{mfaDaysLeft}</b> day{mfaDaysLeft === 1 ? "" : "s"} left to set up MFA.
                    After that, you’ll be redirected here until it’s enabled.
                  </>
                )}
              </div>
            </div>

            <a className="mfa-policy-cta" href="#mfa-setup">
              Set up now
            </a>
          </div>
        </div>
      ) : null}

      {/* Main content */}
      {loading ? (
        <div className="dv-list">
          <div className="card dv-item" style={{ padding: 14 }}>
            <div style={{ fontWeight: 700, marginBottom: 6 }}>Loading…</div>
            <div style={{ color: "#657899" }}>Checking your security status.</div>
          </div>
        </div>
      ) : locked ? (
        <div className="dv-list">
          <div className="card dv-item" style={{ padding: 14 }}>
            <div style={{ fontWeight: 800, color: "#2a0516", marginBottom: 6 }}>
              Verification required
            </div>
            <div style={{ color: "#657899", marginBottom: 12 }}>
              For your safety, you must verify your MFA code to view or change security settings.
            </div>

            {err ? (
              <div
                style={{
                  background: "#fee2e2",
                  border: "1px solid #fecaca",
                  color: "#7a0b0b",
                  borderRadius: 10,
                  padding: "8px 10px",
                  marginBottom: 12,
                }}
              >
                {err}
              </div>
            ) : null}

            <div className="sec-actions-row">
              <button type="button" className="sec-btn sec-btn--primary" onClick={openVerify}>
                Verify to continue
              </button>
              <button type="button" className="sec-btn sec-btn--secondary" onClick={() => setShowDrawer(true)}>
                Read how it works
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div className="dv-list">
          {/* Override master dv-item columns ONLY here */}
          <div id="mfa-setup" className="card dv-item sec-mfa-card" style={{ padding: 14 }}>
            <div>
              <div style={{ fontWeight: 800, marginBottom: 6, color: "#2a0516" }}>
                Two-Factor Authentication (MFA)
              </div>
              <div style={{ color: "#657899", marginBottom: 10 }}>
                Add an extra layer of protection to your account by requiring a code to sign in.
              </div>
            </div>

            <MFASettings />
          </div>
        </div>
      )}

      <VideoLightbox
        open={showVideo}
        onClose={() => setShowVideo(false)}
        url="https://youtu.be/WADU-DSvQy8?si=5pvM0faMmw4wXkOP"
        title="How Security works"
        startAt={0}
        captions={false}
        startMuted={false}
      />

      {/* Drawer */}
      {showDrawer && (
        <div className="dp-drawer-bg" onClick={() => setShowDrawer(false)}>
          <aside className="dp-drawer" onClick={(e) => e.stopPropagation()}>
            <div className="dp-drawer-head">
              <div className="dp-drawer-title">How Security & MFA works</div>
              <div className="dp-drawer-tools">
                <button
                  data-allow-readonly
                  className="btn btn-main"
                  onClick={() => window.print()}
                  type="button"
                >
                  Print
                </button>
                <button
                  data-allow-readonly
                  className="btn btn-cancel"
                  onClick={() => setShowDrawer(false)}
                  type="button"
                >
                  Close
                </button>
              </div>
            </div>

            <div className="dp-drawer-body">
              <p>
                This page lets you enable and manage <b>Two-Factor Authentication (MFA)</b> for your account.
                Once enabled, you’ll need a code (or recovery code) when signing in or making security changes.
              </p>
              <p>
                <b>Trusted device:</b> If you choose “Trust this device”, you won’t be asked for a code on that
                device for a limited time (e.g. 30 days).
              </p>
              <p style={{ marginBottom: 0 }}>
                <b>Extra protection:</b> When MFA is enabled, visiting <code>/security</code> requires verification first.
              </p>
            </div>
          </aside>
        </div>
      )}

      {/* Scoped styles (matches ProofOfLife + fixes dv-item columns + modern buttons) */}
      <style>{`
        .header-row{display:flex;align-items:center;justify-content:space-between;gap:12px}
        .header-actions{display:flex;align-items:center;gap:10px}
        .hero-wrap{margin:6px 0 14px}
        .hero-img{width:100%;height:130px;object-fit:cover;border-radius:14px}
        @media (max-width: 700px){ .hero-img{height:110px} }

        .desktop-only{display:none}.mobile-only{display:block}
        @media(min-width:900px){.desktop-only{display:block}.mobile-only{display:none}}

        .dp-help-row{display:flex;gap:10px;flex-wrap:wrap}
        .dp-help{border-radius:8px;padding:10px 14px;display:inline-flex;align-items:center;gap:10px;font-weight:700;color:#fff;border:none}
        .dp-help.read{background:#650202}
        .dp-help.see{background:#003353}
        .dp-help-ico{font-size:1.05rem}

        /* ✅ Force rows on this page for the MFA card only */
        .sec-mfa-card{
          display:flex !important;
          flex-direction:column !important;
          align-items:stretch !important;
          gap:12px !important;
          overflow:hidden !important;
        }
        .sec-mfa-card > *{min-width:0}
        .sec-mfa-card img{max-width:100% !important; height:auto !important;}

        /* Modern locked-card buttons */
        .sec-actions-row{display:flex;gap:10px;flex-wrap:wrap;margin-top:6px}
        .sec-btn{
          flex:1 1 180px;
          height:44px;
          border-radius:12px;
          font-weight:800;
          cursor:pointer;
          border:1px solid transparent;
          padding:0 14px;
          box-shadow:0 10px 24px rgba(0,0,0,.06);
          transition:transform .12s ease, box-shadow .12s ease;
        }
        .sec-btn:hover{transform:translateY(-1px);box-shadow:0 14px 28px rgba(0,0,0,.08)}
        .sec-btn:active{transform:translateY(0px);box-shadow:0 10px 24px rgba(0,0,0,.06)}
        .sec-btn--primary{background:#2a0516;color:#fff;border-color:#2a0516}
        .sec-btn--secondary{background:#fff;color:#2a0516;border-color:#e5e7eb}
        @media (max-width: 420px){
          .sec-actions-row{flex-direction:column}
          .sec-btn{width:100%}
        }

        /* Drawer */
        .dp-drawer-bg{position:fixed;inset:0;background:rgba(0,0,0,0.3);z-index:10000;display:flex;justify-content:flex-end}
        .dp-drawer{position:fixed;top:56px;right:0;height:calc(100vh - 56px);width:min(520px,95vw);background:#fff;box-shadow:-8px 0 24px rgba(0,0,0,0.15);border-top-left-radius:12px;border-bottom-left-radius:12px;display:flex;flex-direction:column;overflow:hidden}
        .dp-drawer-head{display:flex;align-items:center;justify-content:space-between;gap:10px;padding:14px 16px;border-bottom:1px solid #f0f0f0;background:#fafafa}
        .dp-drawer-title{font-weight:700;color:#2a0516}
        .dp-drawer-tools{display:flex;gap:8px}
        .dp-drawer-body{padding:16px;overflow:auto}

        .mfa-policy-banner{
          margin: 14px 0 6px;
          border-radius: 14px;
          padding: 14px 14px;
          border: 1px solid rgba(0,0,0,0.08);
          box-shadow: 0 10px 24px rgba(0,0,0,0.06);
        }
        .mfa-policy-banner.warning{
          border-color: rgba(140,173,225,0.75);
          background: rgba(140,173,225,0.16);
        }
        .mfa-policy-banner.required{
          border-color: rgba(241,88,34,0.85);
          background: rgba(241,88,34,0.16);
        }
        .mfa-policy-row{
          display:flex;
          align-items:flex-start;
          justify-content:space-between;
          gap: 14px;
        }
        .mfa-policy-title{
          font-weight: 800;
          font-size: 15px;
          color: #2a0516;
          margin-bottom: 4px;
        }
        .mfa-policy-text{
          font-size: 13.5px;
          line-height: 1.45;
          color: #1f2937;
        }
        .mfa-policy-muted{
          opacity: 0.85;
          margin-left: 6px;
        }
        .mfa-policy-cta{
          flex: 0 0 auto;
          text-decoration:none;
          padding: 10px 12px;
          border-radius: 12px;
          background: #2a0516;
          color: #fff;
          font-weight: 800;
          font-size: 13px;
          white-space: nowrap;
          transition: transform 0.12s ease, filter 0.12s ease;
        }
        .mfa-policy-cta:hover{
          transform: translateY(-1px);
          filter: brightness(1.05);
        }
        @media (max-width: 720px){
          .mfa-policy-row{ flex-direction: column; align-items: stretch; }
          .mfa-policy-cta{ text-align:center; }
        }

      
      `}</style>
    </div>
  );
}
